/* eslint-disable @typescript-eslint/naming-convention */
import { Component, Inject, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LocalisationService } from '../../core/services/localisation/zuper.localisation.service';
import { DataCollectorService } from '../../core/services/data_collector_services/data_collector_service.service';
import { JobsService } from '../../core/services/jobs/jobs.service';
import dayjs from 'dayjs';
import { DateFormatPipe } from '../../core/pipes/dateFormat.pipe';
import { UtilityService } from '../../core/services/utility_functions/utility_functions.service';
import { HotToastService } from '@ngneat/hot-toast';
import { BadgeOptions, badgeColorCode } from '../badge/badge.interface';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MyCustomPaginatorIntl } from '../../core/services/paginator_service/my-paginator.service';

@Component({
    selector: 'view-all-recurring-jobs',
    templateUrl: './view-all-recurring-jobs.component.html',
    styleUrls: ['./view-all-recurring-jobs.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [{ provide: MatPaginatorIntl, useClass: MyCustomPaginatorIntl }]
})
export class ViewAllRecurringJobsComponent implements OnInit {
    @Input() recurringJob: any = null;
    @Input() timezone: any = null;

    preferredTimeZone: string = this.utilityService.preferredTimeZone;
    pastJobsParamObj: any = {
        'filter.recurrence_job_uid': '',
        'filter.from_date': '',
        'filter.to_date': '',
        'filter.keyword': '',
        'page': 1,
        'sort': 'DESC',
        'sort_by': 'scheduled_start_time',
        'count': 10
    };
    upcomingJobsParamObj: any = {
        'filter.recurrence_job_uid': '',
        'filter.from_date': '',
        'filter.to_date': '',
        'filter.keyword': '',
        'page': 1,
        'sort': 'ASC',
        'sort_by': 'scheduled_start_time',
        'count': 10
    };
    tabs: any[] = [{
        displayName: 'Past',
        displayKey: 'recurring.jobs_view.tabs.past',
        dataKey: 'PAST_JOBS',
        rel: 'past_jobs'
    }, {
        displayName: 'Upcoming',
        displayKey: 'recurring.jobs_view.tabs.upcoming',
        dataKey: 'UPCOMING_JOBS',
        rel: 'upcoming_jobs'
    }];
    viewMode: string = null;
    recurringPastJobs: any[] = [];
    recurringUpcomingJobs: any[] = [];
    jobsList: any[] = [];
    isLoading: boolean = false;
    searchKey: string = '';
    pastJobsSearchKey: string = '';
    upcomingJobsSearchKey: string = '';
    pagination: any = {
        totalRecords: 0,
        pageIndex: 0,
        pageSize: 10
    };
    pastJobsPagination: any = {
        totalRecords: 0,
        pageIndex: 0,
        pageSize: 10
    };
    upcomingJobsPagination: any = {
        totalRecords: 0,
        pageIndex: 0,
        pageSize: 10
    };
    totalJobs: number = 0;
    displayedColumns: any[] = ['JOB_NO', 'JOB_TITLE', 'CLIENT', 'CATEGORY', 'STATUS', 'PRIORITY', 'SCHEDULED_DATE'];

    constructor(
        @Inject(MAT_DIALOG_DATA) private data: any,
        private dataCollectorService: DataCollectorService,
        private jobsService: JobsService,
        private datePipe: DateFormatPipe,
        private utilityService: UtilityService,
        private toast: HotToastService,
        private localisationService: LocalisationService) {
    }

    ngOnInit(): void {
        this.localisationService.setDefaultLanguage();
        this.recurringJob = this.data?.data ?? this.recurringJob;
        this.timezone = this.data?.timezone;
        this.preferredTimeZone = this.timezone ?? this.utilityService.preferredTimeZone;
        this.updateParamObj();
        this.changeViewMode('UPCOMING_JOBS');
        this.getPastRecurringJobs(1, true);
        this.getUpcomingRecurringJobs(1, true);
    }

    updateParamObj(): void {
        if (this.recurringJob?.recurring_job_uid) {
            this.datePipe.dateFormat = 'YYYY-MM-DD';
            const startDate: string = this.datePipe.transform(this.recurringJob?.job_start);
            const endDate: string = this.datePipe.transform(this.recurringJob?.job_end);
            const today: string = dayjs().format('YYYY-MM-DD');
            const yesterday: string = dayjs().subtract(1, 'day').format('YYYY-MM-DD')

            this.pastJobsParamObj['filter.recurrence_job_uid'] = this.recurringJob?.recurring_job_uid;
            this.pastJobsParamObj['filter.from_date'] = dayjs(startDate).isSame(yesterday) || dayjs(startDate).isBefore(yesterday) ? startDate : yesterday;
            this.pastJobsParamObj['filter.to_date'] = yesterday;
            this.upcomingJobsParamObj['filter.recurrence_job_uid'] = this.recurringJob?.recurring_job_uid;
            this.upcomingJobsParamObj['filter.from_date'] = today;
            this.upcomingJobsParamObj['filter.to_date'] = dayjs(endDate).isSame(today) || dayjs(endDate).isAfter(today) ? endDate : today;
        }
    }

    getPastRecurringJobs(index: number, isInitialCall?: boolean): void {
        if (this.viewMode === 'PAST_JOBS') {
            this.isLoading = true;
        }
        this.pastJobsParamObj.page = index;
        this.dataCollectorService.getData(this.jobsService.jobAPIUrls.jobsUrl, this.pastJobsParamObj).subscribe(
            (response: any) => {
                this.recurringPastJobs = response.data;
                this.pastJobsPagination.totalRecords = response.total_records;
                if (isInitialCall) {
                    this.totalJobs += response.total_records;
                }
                if (this.viewMode === 'PAST_JOBS') {
                    this.jobsList = this.recurringPastJobs;
                    this.isLoading = false;
                    this.pagination = this.pastJobsPagination;
                }
            }, (error: any) => {
            });
    }

    getUpcomingRecurringJobs(index: number, isInitialCall?: boolean): void {
        if (this.viewMode === 'UPCOMING_JOBS') {
            this.isLoading = true;
        }
        this.upcomingJobsParamObj.page = index;
        this.dataCollectorService.getData(this.jobsService.jobAPIUrls.jobsUrl, this.upcomingJobsParamObj).subscribe(
            (response: any) => {
                this.recurringUpcomingJobs = response.data;
                this.upcomingJobsPagination.totalRecords = response.total_records;
                if (isInitialCall) {
                    this.totalJobs += response.total_records;
                }
                if (this.viewMode === 'UPCOMING_JOBS') {
                    this.jobsList = this.recurringUpcomingJobs;
                    this.isLoading = false;
                    this.pagination = this.upcomingJobsPagination;
                }
            }, (error: any) => {
            });
    }

    changeViewMode(mode: string): void {
        if (this.viewMode !== mode) {
            this.viewMode = mode;
            if (this.viewMode === 'PAST_JOBS') {
                this.jobsList = this.recurringPastJobs;
                this.pagination = this.pastJobsPagination;
                this.searchKey = this.pastJobsSearchKey;
            } else {
                this.jobsList = this.recurringUpcomingJobs;
                this.pagination = this.upcomingJobsPagination;
                this.searchKey = this.upcomingJobsSearchKey;
            }
        }
    }

    triggerSearch(searchKey: string): void {
        if (this.viewMode === 'PAST_JOBS') {
            this.pastJobsSearchKey = searchKey ?? '';
            this.pastJobsParamObj['filter.keyword'] = this.pastJobsSearchKey;
            this.pastJobsPagination.pageIndex = 0;
            this.getPastRecurringJobs(1);
        } else {
            this.upcomingJobsSearchKey = searchKey ?? '';
            this.upcomingJobsParamObj['filter.keyword'] = this.upcomingJobsSearchKey;
            this.upcomingJobsPagination.pageIndex = 0;
            this.getUpcomingRecurringJobs(1);
        }
    }

    onPageEvent(event: any): void {
        if (this.viewMode === 'PAST_JOBS') {
            this.pastJobsPagination.pageIndex = event?.pageIndex;
            this.getPastRecurringJobs(event?.pageIndex + 1);
        } else {
            this.upcomingJobsPagination.pageIndex = event?.pageIndex;
            this.getUpcomingRecurringJobs(event?.pageIndex + 1);
        }
    }

    //Badge Options
    getRecurringJobsCountBadge(countType: string): BadgeOptions {
        const badgeOption: BadgeOptions = {
            type: 'PRIMARY',
            displayName: this.getRecurringJobsCount(countType),
            displayType: 'listingCount'
        };
        return badgeOption;
    }

    getRecurringJobsCount(countType: string): number {
        let count: number = 0;
        if (countType === 'PAST_JOBS') {
            count = this.pastJobsPagination?.totalRecords;
        } else if (countType === 'UPCOMING_JOBS') {
            count = this.upcomingJobsPagination?.totalRecords;
        } else if (countType === 'ALL') {
            count = this.totalJobs;
        }
        return count;
    }

    getJobStatusBadge(status: any): BadgeOptions {
        const badgeOption: BadgeOptions = {
            type: status,
            color: status?.status_color || badgeColorCode[status?.status_type],
            displayName: status?.status_name,
            extraClass: 'capitalize'
        };
        return badgeOption;
    }

    getBadgeOptions(status: string): BadgeOptions {
        const newstr = (str): string => {
            if (!this.utilityService.isEmptyOrUndefined(str)) {
                return str.replace(/\_/g, ' ').split(' ')
                    .map(w => w[0].toUpperCase() + w.substring(1).toLowerCase())
                    .join(' ');
            }
        };
        return {
            type: status,
            displayName: `${newstr(status || '')}`,
            extraClass: 'capitalize'
        };
    }
}
